import {
  CHOICES, ROUND_END_REASONS,
  ARTIFACT_EARLY_VALUE, ARTIFACT_LATE_VALUE, ARTIFACTS_AT_EARLY_VALUE,
} from '../constants';
import {
  playersInTemple, gemsOnPath, endRound, revealUntilDecisionMatters,
} from './roundActions';

const clone = (state) => ({
  ...state,
  meta: { ...state.meta, seq: state.meta.seq + 1 },
});

/** "A", "A and B", "A, B and C" - never "A, B" for two people. */
export const listOf = (names) => {
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const logged = (state, text) => ({
  ...state,
  log: [...state.log, { round: state.meta.round, turn: state.meta.turn, text }],
});

/**
 * p2, step 1: one player's secret torch-or-camp choice.
 *
 * Recorded rather than acted on. Nothing at all happens until every player
 * still in the temple has chosen and the turn is resolved - which is what
 * makes it simultaneous.
 */
export const decide = (state, { playerId, choice }) => {
  const player = state.players.find((p) => p.id === playerId);
  if (!player || !player.inTemple) return state;
  if (choice !== CHOICES.CONTINUE && choice !== CHOICES.LEAVE) return state;
  if (player.choice === choice) return state;

  const next = clone(state);
  next.players = next.players.map((p) => (p.id === playerId ? { ...p, choice } : p));
  return next;
};

export const everyoneHasDecided = (state) => (
  !state.meta.roundOver && playersInTemple(state).every((p) => p.choice !== null)
);

/**
 * Takes `amount` of treasure off the path.
 *
 * p3 is explicit that the leftovers can sit anywhere - "the extra treasures
 * are left on any Quest card (which Quest card does not matter)" - so this
 * drains from the oldest card forward and stops. Which card holds the
 * remainder is genuinely arbitrary, and pretending otherwise would invent a
 * rule.
 */
export const takeFromPath = (path, amount) => {
  let remaining = amount;
  return path.map((card) => {
    if (remaining <= 0 || card.left === 0) return card;
    const taken = Math.min(card.left, remaining);
    remaining -= taken;
    return { ...card, left: card.left - taken };
  });
};

/**
 * What each artifact in a batch is worth, given how many have already left.
 * p3: the first three out are 5 points each, any after that are 10 - so a
 * single pickup can straddle the boundary.
 */
export const artifactValues = (alreadyTaken, count) => (
  Array.from({ length: count }, (_, i) => (
    alreadyTaken + i < ARTIFACTS_AT_EARLY_VALUE ? ARTIFACT_EARLY_VALUE : ARTIFACT_LATE_VALUE
  ))
);

/**
 * p2/p3: everyone has chosen, so play the turn out.
 *
 * Order is load-bearing and is the one thing about this game people get
 * wrong: the players who left collect and are GONE before the next card is
 * turned over. A second hazard on that card cannot touch them.
 */
export const resolveTurn = (state) => {
  if (!everyoneHasDecided(state)) return state;

  let next = clone(state);
  const leavers = next.players.filter((p) => p.inTemple && p.choice === CHOICES.LEAVE);

  if (leavers.length) {
    const pot = gemsOnPath(next);
    // p3: "They equally divide all the treasures that have been left on any
    // Quest cards in the path for this round."
    const share = Math.floor(pot / leavers.length);
    next.path = takeFromPath(next.path, share * leavers.length);

    // p3: "If more than one player leaves, no one gets Artifacts! If one and
    // only one player leaves the Temple at this time, that player may pick up
    // all Artifacts that are on the path."
    const alone = leavers.length === 1;
    const claimed = alone ? next.artifactsOnPath : [];
    const claimedWorth = artifactValues(next.artifactsTaken, claimed.length)
      .reduce((sum, v) => sum + v, 0);
    const strandedArtifacts = next.artifactsOnPath.length;

    const leaverIds = new Set(leavers.map((p) => p.id));
    next.players = next.players.map((p) => {
      if (!leaverIds.has(p.id)) return p;
      return {
        ...p,
        // p3: "place all the treasures they received during this round inside
        // their Tent. These treasures are safe, and may not be lost."
        tent: p.tent + p.pocket + share,
        pocket: 0,
        inTemple: false,
        choice: null,
        artifacts: p.artifacts + claimed.length,
        artifactPoints: p.artifactPoints + claimedWorth,
      };
    });

    if (claimed.length) {
      next.artifactsTaken += claimed.length;
      next.artifactsInPlay = next.artifactsInPlay.filter((id) => !claimed.includes(id));
      next.artifactsOnPath = [];
    }

    const names = listOf(leavers.map((p) => p.name));
    const plural = claimed.length > 1 ? 's' : '';
    next = logged(next, alone
      ? `${names} heads back to camp with ${share} from the path${claimed.length ? ` and ${claimed.length} artifact${plural}` : ''}.`
      : `${names} all leave, splitting ${share * leavers.length} between them${strandedArtifacts ? ' - and nobody gets the artifacts' : ''}.`);
  }

  next.players = next.players.map((p) => (p.inTemple ? { ...p, choice: null } : p));

  // p3: "When all players have left the Temple".
  if (!playersInTemple(next).length) return endRound(next, ROUND_END_REASONS.EVERYONE_LEFT);

  return revealUntilDecisionMatters(next);
};
